import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Card, Empty, Space, Drawer, Typography, Tag, 
  Row, Col, Form, Input, Select, DatePicker, 
  Spin, Descriptions, Tabs, List, Badge, Timeline, message, Collapse
} from 'antd';
import { 
  SearchOutlined, EyeOutlined, ClockCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined, PauseCircleOutlined,
  InfoCircleOutlined, FileTextOutlined, TeamOutlined, CopyOutlined
} from '@ant-design/icons';
import { useAuth } from '../utils/auth';
import api from '../services/api';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

// SOP执行记录服务
const sopExecutionService = {
  // 获取执行记录列表
  getExecutions: async (params) => {
    console.log('获取SOP执行记录列表，参数:', params);
    
    try {
      const response = await api.get('/v1/sop/executions', { 
        params,
        headers: {
          'X-Tenant-ID': params.tenantId
        }
      });
      console.log('SOP执行记录列表响应:', response.data);
      return response.data;
    } catch (error) {
      console.error('获取SOP执行记录列表错误:', error);
      throw error;
    }
  },
  
  // 获取执行记录详情
  getExecutionById: async (id, tenantId) => {
    console.log(`获取SOP执行记录详情，ID: ${id}`);
    
    try {
      const response = await api.get(`/v1/sop/executions/${id}`, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('SOP执行记录详情响应:', response.data);
      return response.data;
    } catch (error) {
      console.error(`获取SOP执行记录详情错误:`, error);
      throw error;
    }
  },
  
  // 获取步骤执行记录
  getStepExecutions: async (executionId, tenantId) => {
    console.log(`获取SOP步骤执行记录，执行ID: ${executionId}`);
    
    try {
      const response = await api.get(`/v1/sop/executions/${executionId}/steps`, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('SOP步骤执行记录响应:', response.data);
      return response.data;
    } catch (error) {
      console.error(`获取SOP步骤执行记录错误:`, error);
      throw error;
    }
  },
  
  // 按状态获取步骤执行记录
  getStepExecutionsByStatus: async (executionId, status, tenantId) => {
    console.log(`获取SOP步骤执行记录，执行ID: ${executionId}, 状态: ${status}`);
    
    try {
      const response = await api.get(`/v1/sop/executions/${executionId}/steps/status/${status}`, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log(`状态为 ${status} 的SOP步骤执行记录响应:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`获取SOP步骤执行记录错误:`, error);
      throw error;
    }
  },
  
  // 获取步骤执行详情
  getStepExecutionById: async (stepExecutionId, tenantId) => {
    console.log(`获取SOP步骤执行详情，ID: ${stepExecutionId}`);
    
    try {
      const response = await api.get(`/v1/sop/executions/steps/${stepExecutionId}`, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('SOP步骤执行详情响应:', response.data);
      return response.data;
    } catch (error) {
      console.error(`获取SOP步骤执行详情错误:`, error);
      throw error;
    }
  },
  
  // 获取消息记录详情
  getMessageRecordByStepExecution: async (stepExecutionId, tenantId) => {
    console.log(`获取消息记录详情，步骤执行ID: ${stepExecutionId}`);
    
    try {
      const response = await api.get(`/v1/sop/executions/steps/${stepExecutionId}/message`, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });
      console.log('消息记录详情响应:', response.data);
      return response.data;
    } catch (error) {
      console.error(`获取消息记录详情错误:`, error);
      throw error;
    }
  }
};

// SOP模板服务
const sopTemplateService = {
  // 获取SOP模板列表
  getTemplates: async (params) => {
    console.log('获取推送列表，参数:', params);
    
    try {
      const response = await api.get('/v1/sop/templates', { 
        params,
        headers: {
          'X-Tenant-ID': params.tenantId
        }
      });
      console.log('推送列表响应:', response.data);
      return response.data;
    } catch (error) {
      console.error('获取推送列表错误:', error);
      throw error;
    }
  }
};

const SopExecutions = () => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [currentExecution, setCurrentExecution] = useState(null);
  const [stepExecutions, setStepExecutions] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [searchForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [messageRecords, setMessageRecords] = useState({});
  const [messageLoading, setMessageLoading] = useState({});
  const { tenant } = useAuth();
  
  // 状态选项
  const statusOptions = [
    { value: 'PENDING', label: '待处理', color: 'blue' },
    { value: 'IN_PROGRESS', label: '进行中', color: 'processing' },
    { value: 'COMPLETED', label: '已完成', color: 'success' },
    { value: 'FAILED', label: '失败', color: 'error' },
    { value: 'CANCELED', label: '已取消', color: 'default' }
  ];
  
  // 步骤状态选项
  const stepStatusOptions = [
    { value: 'PENDING', label: '待执行', color: 'blue' },
    { value: 'IN_PROGRESS', label: '执行中', color: 'processing' },
    { value: 'COMPLETED', label: '已完成', color: 'success' },
    { value: 'FAILED', label: '失败', color: 'error' },
    { value: 'SKIPPED', label: '已跳过', color: 'default' }
  ];
  
  // 获取租户ID
  const getTenantId = () => {
    const tenantStr = localStorage.getItem('tenant');
    let tenantId = tenant?.id;
    
    try {
      if (tenantStr) {
        const tenantObj = JSON.parse(tenantStr);
        tenantId = tenantObj.id;
      }
    } catch (error) {
      console.error('解析tenant信息失败:', error);
    }
    
    // 如果没有获取到tenantId，尝试从localStorage直接获取
    if (!tenantId) {
      tenantId = localStorage.getItem('tenantId');
    }
    
    return tenantId;
  };
  
  // 获取执行记录列表
  const fetchExecutions = async (page = pagination.current - 1, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      
      // 检查tenantId
      const tenantId = getTenantId();
      if (!tenantId) {
        message.error('未找到租户信息，请重新登录');
        setLoading(false);
        return;
      }
      
      // 获取表单值
      const values = searchForm.getFieldsValue();
      
      // 构建请求参数
      const params = {
        page,
        size: pageSize,
        tenantId,
        templateId: values.templateId,
        status: values.status,
        startTime: values.timeRange?.[0]?.utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        endTime: values.timeRange?.[1]?.utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
      };
      
      // 过滤掉空值
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === null || params[key] === '') {
          delete params[key];
        }
      });
      
      // 获取执行记录列表
      const result = await sopExecutionService.getExecutions(params);
      
      // 处理响应数据
      let executionList = [];
      let total = 0;
      
      if (Array.isArray(result)) {
        // 直接是数组的情况
        executionList = result;
        total = result.length;
      } else if (result && typeof result === 'object') {
        // 带分页信息的对象
        if (Array.isArray(result.records)) {
          executionList = result.records;
          total = result.totalRecords || 0;
        } else if (result.content && Array.isArray(result.content)) {
          // Spring Data JPA Page格式
          executionList = result.content;
          total = result.totalElements || 0;
        }
      }
      
      console.log('处理后的执行记录:', executionList);
      
      setExecutions(executionList);
      setPagination({
        current: page + 1,
        pageSize,
        total
      });
      
    } catch (error) {
      console.error('获取SOP执行记录列表失败:', error);
      message.error('获取SOP执行记录列表失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 获取SOP模板列表
  const fetchTemplates = async () => {
    try {
      const tenantId = getTenantId();
      if (!tenantId) {
        message.error('未找到租户信息，请重新登录');
        return;
      }
      
      const params = {
        tenantId
      };
      
      const result = await sopTemplateService.getTemplates(params);
      
      let templateList = [];
      
      if (Array.isArray(result)) {
        templateList = result;
      } else if (result && typeof result === 'object' && Array.isArray(result.content)) {
        templateList = result.content;
      }
      
      setTemplates(templateList);
    } catch (error) {
      console.error('获取推送列表失败:', error);
      message.error('获取推送列表失败');
    }
  };
  
  // 组件挂载时获取数据
  useEffect(() => {
    fetchExecutions();
    fetchTemplates();
  }, []);
  
  // 处理搜索
  const handleSearch = (values) => {
    fetchExecutions(0, pagination.pageSize);
  };
  
  // 重置搜索表单
  const handleReset = () => {
    searchForm.resetFields();
    fetchExecutions(0, pagination.pageSize);
  };
  
  // 处理表格变化
  const handleTableChange = (pagination) => {
    fetchExecutions(pagination.current - 1, pagination.pageSize);
  };
  
  // 显示执行记录详情
  const showExecutionDetail = async (record) => {
    try {
      setDrawerVisible(true);
      setDrawerLoading(true);
      
      // 获取执行记录详情
      const executionDetail = await sopExecutionService.getExecutionById(record.id, getTenantId());
      setCurrentExecution(executionDetail);
      
      // 获取步骤执行记录
      const steps = await sopExecutionService.getStepExecutions(record.id, getTenantId());
      setStepExecutions(steps);
      
      // 自动获取所有消息发送类型步骤的消息记录
      const messageSteps = steps.filter(step => step.actionType === "SEND_MESSAGE");
      if (messageSteps.length > 0) {
        // 创建一个Promise数组，用于并行请求所有消息记录
        const messagePromises = messageSteps.map(async (step) => {
          try {
            const messageRecord = await sopExecutionService.getMessageRecordByStepExecution(
              step.id, 
              getTenantId()
            );
            
            if (messageRecord) {
              return { stepId: step.id, record: messageRecord };
            }
            return null;
          } catch (error) {
            console.error(`获取步骤 ${step.id} 的消息记录失败:`, error);
            return null;
          }
        });
        
        // 等待所有消息记录请求完成
        const messageResults = await Promise.all(messagePromises);
        
        // 更新消息记录状态
        const newMessageRecords = {};
        messageResults.forEach(result => {
          if (result) {
            newMessageRecords[result.stepId] = result.record;
          }
        });
        
        setMessageRecords(newMessageRecords);
      }
      
    } catch (error) {
      console.error('获取SOP执行记录详情失败:', error);
      message.error('获取SOP执行记录详情失败');
    } finally {
      setDrawerLoading(false);
    }
  };
  
  // 关闭抽屉
  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setCurrentExecution(null);
    setStepExecutions([]);
    setMessageRecords({});
  };
  
  // 获取消息记录
  const fetchMessageRecord = async (stepExecution) => {
    // 只获取消息发送类型的步骤执行记录的消息记录
    if (stepExecution.actionType !== "SEND_MESSAGE") {
      return;
    }
    
    try {
      // 设置加载状态
      setMessageLoading(prev => ({
        ...prev,
        [stepExecution.id]: true
      }));
      
      // 获取消息记录
      const messageRecord = await sopExecutionService.getMessageRecordByStepExecution(
        stepExecution.id, 
        getTenantId()
      );
      
      if (messageRecord) {
        // 更新消息记录状态
        setMessageRecords(prev => ({
          ...prev,
          [stepExecution.id]: messageRecord
        }));
      }
    } catch (error) {
      console.error(`获取步骤 ${stepExecution.id} 的消息记录失败:`, error);
      message.error(`获取消息记录失败: ${error.message}`);
    } finally {
      // 清除加载状态
      setMessageLoading(prev => ({
        ...prev,
        [stepExecution.id]: false
      }));
    }
  };
  
  // 渲染状态标签
  const renderStatusTag = (status) => {
    const statusOption = statusOptions.find(option => option.value === status);
    
    if (statusOption) {
      return (
        <Tag color={statusOption.color}>
          {statusOption.label}
        </Tag>
      );
    }
    
    return <Tag>{status}</Tag>;
  };
  
  // 渲染步骤状态标签
  const renderStepStatusTag = (status) => {
    const statusOption = stepStatusOptions.find(option => option.value === status);
    
    if (statusOption) {
      return (
        <Tag color={statusOption.color}>
          {statusOption.label}
        </Tag>
      );
    }
    
    return <Tag>{status}</Tag>;
  };
  
  // 渲染时间
  const renderTime = (time) => {
    return time ? moment(time).format('YYYY-MM-DD HH:mm:ss') : '-';
  };
  
  // 表格列定义
  const columns = [
    {
      title: '推送名称',
      dataIndex: 'templateName',
      key: 'templateName',
    },
    {
      title: '目标名称',
      dataIndex: 'targetName',
      key: 'targetName',
    },
    {
      title: '触发人',
      dataIndex: 'triggerOperatorName',
      key: 'triggerOperatorName',
      render: (name, record) => {
        if (name) {
          return name;
        }
        // 如果没有姓名，显示触发人ID
        if (record.triggerOperatorId) {
          return record.triggerOperatorId;
        }
        return '-';
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: renderStatusTag,
    },
    {
      title: '进度',
      key: 'progress',
      render: (_, record) => {
        const total = record.totalSteps || 0;
        const completed = record.completedSteps || 0;
        const failed = record.failedSteps || 0;
        const skipped = record.skippedSteps || 0;
        
        if (total === 0) {
          return '-';
        }
        
        return (
          <span>
            {completed + failed + skipped}/{total}
            {failed > 0 && <span style={{ color: '#f5222d', marginLeft: 8 }}>{failed} 失败</span>}
          </span>
        );
      },
    },
    {
      title: '开始时间',
      dataIndex: 'startTime',
      key: 'startTime',
      render: renderTime,
    },
    {
      title: '结束时间',
      dataIndex: 'endTime',
      key: 'endTime',
      render: renderTime,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: renderTime,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => showExecutionDetail(record)}
        >
          详情
        </Button>
      ),
    },
  ];
  
  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2}>推送执行明细</Title>
        </Col>
        <Col>
          <Space>
            <Button onClick={handleReset}>重置</Button>
            <Button type="primary" onClick={() => searchForm.submit()}>查询</Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Form
          form={searchForm}
          layout="horizontal"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="templateId" label="推送">
                <Select
                  placeholder="选择推送"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {templates.map(template => (
                    <Option key={template.id} value={template.id}>
                      {template.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="状态">
                <Select
                  placeholder="选择状态"
                  allowClear
                >
                  {statusOptions.map(status => (
                    <Option key={status.value} value={status.value}>
                      {status.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="timeRange" label="时间范围">
                <RangePicker showTime format="YYYY-MM-DD HH:mm:ss" />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handleReset}>
                重置
              </Button>
            </Col>
          </Row>
        </Form>
        
        <Table
          columns={columns}
          dataSource={executions}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Card>
      
      {/* 执行记录详情抽屉 */}
      <Drawer
        title="推送执行详情"
        placement="right"
        width={800}
        onClose={handleDrawerClose}
        open={drawerVisible}
      >
        <Spin spinning={drawerLoading}>
          {currentExecution ? (
            <>
              <Descriptions
                title="基本信息"
                bordered
                column={2}
                size="small"
                style={{ marginBottom: 24 }}
              >
                <Descriptions.Item label="执行ID">
                  {currentExecution.id}
                </Descriptions.Item>
                <Descriptions.Item label="模板名称">
                  {currentExecution.templateName}
                </Descriptions.Item>
                <Descriptions.Item label="目标类型">
                  {currentExecution.targetType === 'CUSTOMER' ? '客户' : 
                   currentExecution.targetType === 'GROUP' ? '客户群' : 
                   currentExecution.targetType}
                </Descriptions.Item>
                <Descriptions.Item label="目标名称">
                  {currentExecution.targetName}
                </Descriptions.Item>
                <Descriptions.Item label="触发类型">
                  {currentExecution.triggerType === 'TAG_CHANGE' ? '标签变更' : 
                   currentExecution.triggerType === 'MANUAL' ? '手动触发' : 
                   currentExecution.triggerType}
                </Descriptions.Item>
                <Descriptions.Item label="触发人">
                  {currentExecution.triggerOperatorName || currentExecution.triggerOperatorId || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="执行状态">
                  {renderStatusTag(currentExecution.status)}
                </Descriptions.Item>
                <Descriptions.Item label="开始时间">
                  {renderTime(currentExecution.startTime)}
                </Descriptions.Item>
                <Descriptions.Item label="结束时间">
                  {renderTime(currentExecution.endTime)}
                </Descriptions.Item>
                <Descriptions.Item label="总步骤数">
                  {currentExecution.totalSteps || 0}
                </Descriptions.Item>
                <Descriptions.Item label="已完成步骤">
                  {currentExecution.completedSteps || 0}
                </Descriptions.Item>
                <Descriptions.Item label="失败步骤">
                  {currentExecution.failedSteps || 0}
                </Descriptions.Item>
                <Descriptions.Item label="跳过步骤">
                  {currentExecution.skippedSteps || 0}
                </Descriptions.Item>
                <Descriptions.Item label="创建时间" span={2}>
                  {renderTime(currentExecution.createdAt)}
                </Descriptions.Item>
              </Descriptions>
              
              <Tabs defaultActiveKey="steps">
                <TabPane tab="步骤执行记录" key="steps">
                  {stepExecutions.length > 0 ? (
                    <Timeline mode="left">
                      {stepExecutions.sort((a, b) => {
                        // 首先比较executeTime
                        if (a.executeTime && b.executeTime) {
                          return new Date(a.executeTime) - new Date(b.executeTime);
                        }
                        // 如果executeTime为空，将其放在后面
                        if (!a.executeTime) return 1;
                        if (!b.executeTime) return -1;
                        // 如果都为空，则按照原来的stepOrder排序
                        return a.stepOrder - b.stepOrder;
                      }).map(step => (
                        <Timeline.Item
                          key={step.id}
                          dot={
                            step.status === 'COMPLETED' ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                            step.status === 'FAILED' ? <CloseCircleOutlined style={{ color: '#f5222d' }} /> :
                            step.status === 'IN_PROGRESS' ? <ClockCircleOutlined style={{ color: '#1890ff' }} /> :
                            step.status === 'SKIPPED' ? <PauseCircleOutlined style={{ color: '#d9d9d9' }} /> :
                            <ClockCircleOutlined style={{ color: '#1890ff' }} />
                          }
                          color={
                            step.status === 'COMPLETED' ? 'green' :
                            step.status === 'FAILED' ? 'red' :
                            step.status === 'IN_PROGRESS' ? 'blue' :
                            step.status === 'SKIPPED' ? 'gray' : 'blue'
                          }
                        >
                          <Collapse ghost>
                            <Panel 
                              header={
                                <Space>
                                  <span>步骤 {step.stepOrder}: {step.stepName}</span>
                                  {renderStepStatusTag(step.status)}
                                </Space>
                              } 
                              key={step.id}
                            >
                              <Descriptions column={1} size="small">
                                <Descriptions.Item label="步骤类型">
                                  {step.stepType === 'MESSAGE' ? '发送消息' : 
                                   step.stepType === 'TAG' ? '更新标签' : 
                                   step.stepType === "MOMENT" ? '发送朋友圈' : 
                                   step.stepType}
                                </Descriptions.Item>
                                <Descriptions.Item label="动作类型">
                                  {step.actionType}
                                </Descriptions.Item>
                                <Descriptions.Item label="计划执行时间">
                                  {renderTime(step.executeTime)}
                                </Descriptions.Item>
                                <Descriptions.Item label="开始执行时间">
                                  {renderTime(step.startTime)}
                                </Descriptions.Item>
                                <Descriptions.Item label="完成时间">
                                  {renderTime(step.completionTime)}
                                </Descriptions.Item>
                                
                                {step.status === 'COMPLETED' && (
                                  <Descriptions.Item label="执行结果">
                                    <Text
                                      style={{ maxWidth: '100%' }}
                                      ellipsis={{ tooltip: step.result }}
                                    >
                                      {step.result || '(无结果数据)'}
                                    </Text>
                                  </Descriptions.Item>
                                )}
                                {step.status === 'FAILED' && (
                                  <Descriptions.Item label="错误信息">
                                    <Space>
                                      <Text type="danger">
                                        {step.errorMessage || '未知错误'}
                                      </Text>
                                    </Space>
                                  </Descriptions.Item>
                                )}
                                {step.status === 'FAILED' && step.retryCount > 0 && (
                                  <Descriptions.Item label="重试次数">
                                    {step.retryCount}/{step.maxRetries || '-'}
                                  </Descriptions.Item>
                                )}
                              </Descriptions>
                              
                              {step.actionType === 'SEND_MESSAGE' && (
                                <div style={{ marginTop: '16px' }}>
                                  {messageRecords[step.id] ? (
                                    <Collapse defaultActiveKey={['messageDetails']}>
                                      <Panel header="消息详情" key="messageDetails">
                                        <Tabs defaultActiveKey="basicInfo">
                                          <TabPane tab="基本信息" key="basicInfo">
                                            <Descriptions bordered size="small" column={1}>
                                              <Descriptions.Item label="消息ID">
                                                {messageRecords[step.id].id}
                                              </Descriptions.Item>
                                              <Descriptions.Item label="消息状态">
                                                <Tag color={
                                                  messageRecords[step.id].status === 'SENT' ? 'green' : 
                                                  messageRecords[step.id].status === 'FAILED' ? 'red' : 'blue'
                                                }>
                                                  {messageRecords[step.id].status === 'SENT' ? '已发送' : 
                                                   messageRecords[step.id].status === 'FAILED' ? '发送失败' : 
                                                   messageRecords[step.id].status}
                                                </Tag>
                                              </Descriptions.Item>
                                              <Descriptions.Item label="接收者">
                                                {messageRecords[step.id].receiver}
                                              </Descriptions.Item>
                                              <Descriptions.Item label="发送时间">
                                                {renderTime(messageRecords[step.id].sendTime)}
                                              </Descriptions.Item>
                                              {messageRecords[step.id].errorMessage && (
                                                <Descriptions.Item label="错误原因">
                                                  <Text type="danger">
                                                    {messageRecords[step.id].errorMessage}
                                                  </Text>
                                                </Descriptions.Item>
                                              )}
                                              {step.result && (
                                                <Descriptions.Item label="执行结果">
                                                  <pre style={{ 
                                                    maxHeight: '200px', 
                                                    overflow: 'auto', 
                                                    background: '#f5f5f5', 
                                                    padding: '10px',
                                                    borderRadius: '4px',
                                                    fontSize: '12px'
                                                  }}>
                                                    {(() => {
                                                      try {
                                                        // 尝试格式化JSON
                                                        const parsed = JSON.parse(step.result);
                                                        return JSON.stringify(parsed, null, 2);
                                                      } catch (e) {
                                                        // 不是有效的JSON，直接返回原始内容
                                                        return step.result;
                                                      }
                                                    })()}
                                                  </pre>
                                                </Descriptions.Item>
                                              )}
                                            </Descriptions>
                                          </TabPane>
                                          
                                          {messageRecords[step.id]?.weworkMessageRecord && (
                                            <TabPane tab="企业微信API响应" key="weworkResponse">
                                              <Collapse defaultActiveKey={['basicInfo', 'rawResponse']}>
                                                <Panel header="基本信息" key="basicInfo">
                                                  <Descriptions bordered size="small" column={1}>
                                                    <Descriptions.Item label="企业微信消息ID">
                                                      {messageRecords[step.id].weworkMessageRecord.msgId || '-'}
                                                    </Descriptions.Item>
                                                    {messageRecords[step.id].weworkMessageRecord.status && (
                                                      <Descriptions.Item label="企业微信消息状态">
                                                        <Tag color={
                                                          messageRecords[step.id].weworkMessageRecord.status === 'SENT' ? 'green' : 
                                                          messageRecords[step.id].weworkMessageRecord.status === 'FAILED' ? 'red' : 'blue'
                                                        }>
                                                          {messageRecords[step.id].weworkMessageRecord.status}
                                                        </Tag>
                                                      </Descriptions.Item>
                                                    )}
                                                    {messageRecords[step.id].weworkMessageRecord.failedReceivers && (
                                                      <Descriptions.Item label="发送失败接收者">
                                                        <pre style={{ 
                                                          maxHeight: '100px', 
                                                          overflow: 'auto', 
                                                          background: '#f5f5f5', 
                                                          padding: '10px',
                                                          borderRadius: '4px',
                                                          fontSize: '12px'
                                                        }}>
                                                          {(() => {
                                                            try {
                                                              const parsed = JSON.parse(messageRecords[step.id].weworkMessageRecord.failedReceivers);
                                                              return JSON.stringify(parsed, null, 2);
                                                            } catch (e) {
                                                              return messageRecords[step.id].weworkMessageRecord.failedReceivers;
                                                            }
                                                          })()}
                                                        </pre>
                                                      </Descriptions.Item>
                                                    )}
                                                  </Descriptions>
                                                </Panel>
                                                
                                                {messageRecords[step.id].weworkMessageRecord.rawResponse && (
                                                  <Panel header="原始API响应内容" key="rawResponse">
                                                    <div style={{ position: 'relative' }}>
                                                      <pre style={{ 
                                                        maxHeight: '400px', 
                                                        overflow: 'auto', 
                                                        background: '#f5f5f5', 
                                                        padding: '16px',
                                                        borderRadius: '4px',
                                                        fontSize: '13px',
                                                        fontFamily: 'monospace'
                                                      }}>
                                                        {(() => {
                                                          try {
                                                            // 尝试格式化JSON
                                                            const parsed = JSON.parse(messageRecords[step.id].weworkMessageRecord.rawResponse);
                                                            return JSON.stringify(parsed, null, 2);
                                                          } catch (e) {
                                                            // 不是有效的JSON，直接返回原始内容
                                                            return messageRecords[step.id].weworkMessageRecord.rawResponse;
                                                          }
                                                        })()}
                                                      </pre>
                                                      <Button 
                                                        size="small" 
                                                        type="text" 
                                                        icon={<CopyOutlined />} 
                                                        style={{ position: 'absolute', top: '8px', right: '8px' }}
                                                        onClick={() => {
                                                          try {
                                                            navigator.clipboard.writeText(messageRecords[step.id].weworkMessageRecord.rawResponse);
                                                            message.success('已复制到剪贴板');
                                                          } catch (e) {
                                                            message.error('复制失败');
                                                          }
                                                        }}
                                                      >
                                                        复制
                                                      </Button>
                                                    </div>
                                                  </Panel>
                                                )}
                                              </Collapse>
                                            </TabPane>
                                          )}
                                        </Tabs>
                                      </Panel>
                                    </Collapse>
                                  ) : (
                                    <Spin tip="加载消息记录中..." />
                                  )}
                                </div>
                              )}
                            </Panel>
                          </Collapse>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  ) : (
                    <Empty description="暂无步骤执行记录" />
                  )}
                </TabPane>
              </Tabs>
            </>
          ) : (
            <Empty description="加载中或无数据" />
          )}
        </Spin>
      </Drawer>
    </div>
  );
};

export default SopExecutions; 